<template>
  <div class="item-prices columns" v-if="ready">
    <!-- price schemes -->
    <div class="price-scheme-prices column">
      <div class="subtitle" v-if="moduleActivated.POS">{{$t('items.prices_online_ordering_kiosk')}}</div>

      <!-- basic item with no variants -->
      <template v-if="rootItem">
        <div class="group --red" v-if="item.variants.length > 0 & $store.getters.loggedUserHasSuperAdmin && isMenuboardActivated"
             v-html="$t('items.variants_single_price_disclaimer')"></div>
        <div class="group" v-for="(priceScheme, priceSchemeIndex) in item.priceSchemes" :key="priceSchemeIndex">
          <label :class="{'--red': item.variants.length > 0}">{{$t(`scheme_methods.${priceScheme.method}`)}} ({{currencySymbol}})</label>
          <input type="number" v-model.number="priceScheme.unitPrice" step="0.01"
                 :class="{invalid: validate ? validate.error('item.priceSchemes[' + priceSchemeIndex + '].unitPrice') : false}">
        </div>
      </template>

      <!-- anything else -->
      <template v-if="!rootItem">
        <div class="group" v-for="(priceScheme, priceSchemeIndex) in item.priceSchemes" :key="priceSchemeIndex">
          <label>{{$t(`scheme_methods.${priceScheme.method}`)}} ({{currencySymbol}})</label>
          <input type="number" v-model.number="priceScheme.unitPrice" step="0.01"
                 :class="{invalid: validate ? validate.error(priceSchemeValidationRootName + '.priceSchemes[' + priceSchemeIndex + '].unitPrice') : false}">
        </div>
      </template>

      <!-- free item warning if configured -->
      <template v-if="productHasNoPrice(item) && !canDisplayFreeItems && showFreeItemWarning">
        <br>
        <TipBox danger no-border :title="$t('tips.product_no_price.title')">
          <span v-html="$t('tips.product_no_price.description')"></span>
        </TipBox>
      </template>
    </div>

    <!-- revenue centers -->
    <div class="revenue-center-prices column" v-if="moduleActivated.POS">
      <div class="subtitle">{{$t('items.prices_pos')}}</div>
      <table class="revenue-center-prices__table">
        <tr class="header-row">
          <th v-if="hasVariants">{{$t('items.variants')}}</th>
          <th>{{$t('revenue_centers.entity')}}</th>
          <th class="price-header">{{$t('unitPrice')}}</th>
        </tr>
        <template v-if="hasVariants">
          <template v-for="(variant, variantIndex) of item.variants" v-if="variant.revenueCenter && variant.revenueCenter.prices">
            <tr v-for="revenueCenterRef of Object.keys(variant.revenueCenter.prices)" :key="'variant-' + variantIndex + revenueCenterRef">
              <td>{{$to(variant.name)}}</td>
              <td>{{getRevenueCenterName(revenueCenterRef)}}</td>
              <td>
                <div class="price">
                  <input type="number" v-model.number="variant.revenueCenter.prices[revenueCenterRef].unitPrice" step="0.01" placeholder="00.00" min="0">
                </div>
              </td>
            </tr>
          </template>
        </template>
        <template v-if="!hasVariants">
          <tr v-for="revenueCenterRef of Object.keys(item.revenueCenter.prices)" :key="'rc-' + revenueCenterRef">
            <td>{{getRevenueCenterName(revenueCenterRef)}}</td>
            <td>
              <div class="price">
                <div class="currency-symbol">{{currencySymbol}}</div>
                <input type="number" v-model.number="item.revenueCenter.prices[revenueCenterRef].unitPrice" step="0.01" placeholder="00.00" min="0">
              </div>
            </td>
          </tr>
        </template>
      </table>
    </div>
  </div>
</template>

<script>
  import {mapActions} from "vuex";

  export default {
    name: "ItemPrices",

    props: {
      item: Object,
      rootItem: Boolean,
      showFreeItemWarning: Boolean,
      priceSchemeValidationRootName: {
        type: String,
        default: "item"
      },
      validate: Object
    },

    data() {
      return {
        revenueCenters: [],
        moduleActivated: {},
        currencySymbol: "",
        ready: false,
        /**
         * Sample Data:
         *
         * {
         *   "revenueCenter": {
         *     "prices": {
         *       "$ref1": {"unitPrice": 10.00},
         *       "$ref2": {"unitPrice": 10.00},
         *       "$ref1_$refmode1": {"unitPrice": 10.00},
         *     }
         *   }
         * }
         */
      }
    },

    computed: {
      companyId() {
        return this.$route.query.company;
      },
      isMenuboardActivated() {
        return this.moduleActivated && this.moduleActivated.menuBoard;
      },
      hasVariants() {
        return this.item && this.item.variants && this.item.variants.length > 0;
      },
      canDisplayFreeItems() {
        return this.$store.state.contextInventory && this.$store.state.contextInventory.showItemsWhenZero;
      },
    },

    async mounted() {
      if (!this.item) {
        return;
      }
      await this.loadRevenueCenters();
      this.generateMissingRevenueCenters();
      this.moduleActivated = await this.$store.getters.getContextCompanyActivatedModule;
      this.currencySymbol = await this.$store.getters.getContextCurrencySymbol;
      this.ready = true;
    },

    methods: {
      ...mapActions(["getRevenueCenters"]),

      async loadRevenueCenters() {
        let response = await this.getRevenueCenters(this.companyId);
        if (response && response.revenueCenters) {
          this.revenueCenters = response.revenueCenters;
        }
      },

      productHasNoPrice(obj) {
        return obj.priceSchemes.filter(p => parseFloat(p.unitPrice) > 0).length === 0;
      },

      generateMissingRevenueCenters() {
        if (!this.item.revenueCenter) {
          return;
        }
        if (!this.item.revenueCenter.prices) {
          this.item.revenueCenter.prices = {};
        }
        for (let revenueCenter of this.revenueCenters) {
          let refs = Object.keys(this.item.revenueCenter.prices);
          if (refs.indexOf(revenueCenter.ref) === -1) {
            this.item.revenueCenter.prices[revenueCenter.ref] = { unitPrice: "" };
          }
        }

        if (this.item.variants) {
          for (let i = 0; i < this.item.variants.length; i++) {
            if (!this.item.variants[i].revenueCenter.prices) {
              this.item.variants[i].revenueCenter.prices = {};
            }
            for (let revenueCenter of this.revenueCenters) {
              let refs = Object.keys(this.item.variants[i].revenueCenter.prices);
              if (refs.indexOf(revenueCenter.ref) === -1) {
                this.item.variants[i].revenueCenter.prices[revenueCenter.ref] = {unitPrice: ""};
              }
            }
          }
        }
      },

      getRevenueCenterName(ref) {
        let revenueCenter = this.revenueCenters.find(r => r.ref === ref);
        if (revenueCenter) {
          return this.$to(revenueCenter.name);
        }
        return ref;
      }
    }
  }
</script>

<style lang="scss" scoped>
  .item-prices {
    width: 100%;

    .subtitle {
      font-size: 14px;
    }
  }
  .revenue-center-prices__table {
    width: 100%;
    text-align: center;
    border-collapse: collapse;
    table-layout:fixed;

    .header-row {
      th {
        font-size: 14px;
        padding-top: 0;
      }
      .price-header {
        text-align: right;
        padding-right: 30px;
      }
    }

    td {
      border-top: 1px solid #e6e6e6;
    }

    th, td {
      padding: 10px 15px;
      width: 100%;
      display:table-cell;
    }

    .price, input {
      width: 100%;
    }

    input {
      text-align: right;
      padding: 8px 10px 8px 50px;
      line-height: 1;
    }

    .price {
      text-align: right;
      position: relative;

      .currency-symbol {
        position: absolute;
        color: #808080;
        font-size: 14px;
        left: 10px;
        top: 50%;
        transform: translateY(-50%);
      }
    }
  }
</style>